import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Space, message, Image, Tag, Modal, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
// 补充配套图标，统一视觉
import { DeleteOutlined, ArrowLeftOutlined, UploadOutlined, ReloadOutlined, WarningOutlined, PictureOutlined } from '@ant-design/icons';
import { getImages, deleteImage } from '@/api/panelImages';
import { getPanel } from '@/api/panels';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { PanelImage } from '@/types/panelImage';

export const ImageList = () => {
  const { panelId } = useParams<{ panelId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [activePreviewId, setActivePreviewId] = useState<string>('');
  // 添加分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const panelQuery = useQuery({
    queryKey: ['panel', developerId, panelId],
    queryFn: () => {
      if (!panelId) throw new Error('面板 ID 不能为空');
      return getPanel(panelId);
    },
    enabled: !!panelId && !!developerId,
    staleTime: 30000,
  });

  const imageQuery = useQuery({
    queryKey: ['panel-images', developerId, panelId],
    queryFn: async (): Promise<PanelImage[]> => {
      if (!panelId) throw new Error('面板 ID 不能为空');
      const response = await getImages(panelId);
      return response.images;
    },
    enabled: !!panelId && !!developerId,
    staleTime: 0,
    gcTime: 0,
  });

  const images: PanelImage[] = useMemo(() => {
    if (!imageQuery.data) return [];
    
    // 按 created_at 倒序排序（最新在前）
    return [...imageQuery.data].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [imageQuery.data]);

  const panel = panelQuery.data;
  const loading = panelQuery.isLoading || imageQuery.isLoading;
  const error = panelQuery.error || imageQuery.error;

  const deleteMutation = useMutation({
    mutationFn: (filename: string) => {
      if (!panelId) throw new Error('面板 ID 不能为空');
      return deleteImage(panelId, filename);
    },
    onSuccess: () => {
      message.success('图片删除成功');
      queryClient.invalidateQueries({ queryKey: ['panel-images', developerId, panelId] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '删除失败，请检查是否有产品关联该图片。';
      message.error(errorMessage);
    },
  });

  const handleRefresh = async () => {
    message.loading('正在刷新图片列表...', 0);
    await imageQuery.refetch();
    message.destroy();
    message.success('图片列表已刷新');
  };

  const handleBack = () => navigate(`/panels/${panelId}`);
  const handleUpload = () => navigate(`/panel-images/upload?panelId=${panelId}`);

  const handleDelete = (filename: string) => {
    Modal.confirm({
      title: '确认删除图片',
      icon: <WarningOutlined className="text-orange-500" />,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      content: (
        <div className="space-y-2">
          <p>确定要删除图片 <span className="font-mono text-red-500">{filename}</span> 吗？</p>
          <p className="text-red-500 font-medium">此操作不可恢复，已关联该图片的面板将无法正常展示！</p>
        </div>
      ),
      onOk: () => deleteMutation.mutate(filename),
    });
  };

  const handlePreview = (_: string, id: string) => {
    setPreviewVisible(true);
    setActivePreviewId(id);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="加载失败" description="无法加载图片列表，请检查网络或稍后重试。" className="mb-8" />;

  // 图片类型色值映射，提升视觉区分度
  const typeColorMap: Record<string, string> = {
    PNG: 'blue',
    JPG: 'green',
    JPEG: 'green',
    WEBP: 'purple',
    GIF: 'orange',
  };

  const columns: ColumnsType<PanelImage> = [
    {
      title: '图片预览',
      key: 'preview',
      width: 120,
      align: 'center' as const,
      render: (_, record) => (
        <div className="flex items-center justify-center">
          <Image
            src={record.file_url}
            alt={record.filename}
            width={72}
            height={72}
            preview={{
              visible: previewVisible && activePreviewId === record.id,
              onVisibleChange: (visible) => {
                if (!visible) {
                  setPreviewVisible(false);
                  setActivePreviewId('');
                }
              },
              src: record.file_url,
              mask: <div className="text-white font-medium">{record.filename}</div>,
            }}
            fallback="image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhESMIAAAAABJRU5ErkJggg=="
            className="object-cover rounded-lg border border-gray-100 shadow-sm cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handlePreview(record.file_url, record.id)}
            onError={() => {
              message.warning('图片加载失败，请点击"刷新列表"获取最新链接。');
            }}
          />
        </div>
      ),
    },
    {
      title: '文件名称',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
      render: (text: string) => (
        <span className="font-mono text-sm text-gray-800 break-all max-w-md">{text}</span>
      ),
    },
    {
      title: '图片版本',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      align: 'center' as const,
      render: (version: number) => <Tag color="blue" className="px-2">{version}</Tag>,
    },
    {
      title: '文件类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 110,
      align: 'center' as const,
      render: (type: string) => {
        const ext = type.split('/')[1]?.toUpperCase() || type;
        const color = typeColorMap[ext] || 'gray';
        return <Tag color={color}>{ext}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      align: 'center' as const,
      render: (createdAt: string) => {
        // 格式化 ISO 时间为更友好的格式，例如：2026-02-04 15:30:00
        return new Date(createdAt).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      align: 'center' as const,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            size="small" 
            icon={<DeleteOutlined className="text-xs" />}
            danger
            onClick={() => handleDelete(record.filename)}
            loading={deleteMutation.isPending && deleteMutation.variables === record.filename}
            className="hover:shadow-sm transition-shadow"
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full space-y-8">
      {/* 标题+操作区 - 适配小屏，统一层级 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <PictureOutlined className="text-blue-500" />
            面板图片管理
          </h1>
          {panel && (
            <p className="text-gray-600 mt-1 flex items-center gap-2">
              所属面板: 
              <Tag color="blue" className="px-3 py-1">{panel.name}</Tag>
              <Tag color="purple" className="px-2 py-1">v{panel.version}</Tag>
            </p>
          )}
          <p className="text-gray-500 mt-1">
            共 <span className="font-medium text-blue-600">{images.length}</span> 张图片资源
          </p>
        </div>
        <Space size="middle" wrap>
          <Button 
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回面板详情
          </Button>
          <Button 
            onClick={handleRefresh} 
            icon={<ReloadOutlined />} 
            disabled={imageQuery.isFetching}
            loading={imageQuery.isFetching}
            className="hover:bg-gray-50 transition-colors"
          >
            刷新列表
          </Button>
          <Button 
            type="primary" 
            onClick={handleUpload}
            icon={<UploadOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            上传图片
          </Button>
        </Space>
      </div>

      {/* 重要提示 - 优化样式和间距 */}
      <Alert
        message="重要操作提示"
        description={
          <div className="space-y-1 text-gray-700">
            <p>• <strong className="text-orange-600">上传同名文件会覆盖旧版本</strong>（版本号自动递增），旧文件将被永久删除</p>
            <p>• 覆盖上传后系统会自动刷新列表，立即显示最新图片数据。</p>
            <p>• 如遇图片"加载失败"，请点击「刷新列表」获取最新访问链接。</p>
            <p>• <strong className="text-red-600">删除操作不可恢复</strong>，请确认无产品关联后再操作。</p>
          </div>
        }
        type="warning"
        showIcon
        className="rounded-lg shadow-sm"
        banner
      />

      <br />

      {/* 图片列表卡片 - 统一无边框样式 */}
      <Card 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{ padding: '0px' } as React.CSSProperties}
      >
        {images.length === 0 ? (
          <div className="text-center py-16 flex flex-col items-center justify-center">
            <div className="text-8xl text-gray-200 mb-6">🖼️</div>
            <p className="text-gray-500 text-lg mb-2">暂无图片资源</p>
            <p className="text-gray-400 text-sm mb-8">上传后的图片将在此处展示，支持预览、下载和删除。</p>
            <Button 
              type="primary" 
              icon={<UploadOutlined />} 
              size="large" 
              onClick={handleUpload}
              className="px-8 py-2 hover:shadow-md transition-shadow"
            >
              上传第一张图片
            </Button>
          </div>
        ) : (
          <Table
            // 使用分页参数作为 key 的一部分，确保切换分页大小时重新渲染
            key={`table-${pagination.pageSize}-${pagination.current}`}
            columns={columns}
            dataSource={images}
            rowKey="id"
            loading={imageQuery.isFetching}
            pagination={{
              ...pagination,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `共 ${total} 张图片`,
              className: 'py-4',
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize: pageSize || pagination.pageSize,
                });
              },
            }}
            bordered={false}
            size="middle"
            scroll={{ x: 'max-content' }}
            style={{
              '--ant-table-row-hover-bg': '#f5faff',
              '--ant-table-header-text-color': '#667085',
              '--ant-table-border-color': '#e8e8e8'
            } as React.CSSProperties}
          />
        )}
      </Card>
    </div>
  );
};